/**
 * As três conferências do diagrama, numa chamada só.
 *
 * **Modelo** — a mesma conferência que hoje só roda na hora de publicar. Vista
 * aqui, ela deixa de ser surpresa no fim: dá para arrumar antes.
 * **Quantidade de ligações** — quantas o diagrama desenha contra quantos pinouts
 * o projeto tem, já descontadas as restrições dos modelos.
 * **Sem ligação** — componentes que estão no diagrama e não aparecem no desenho.
 *
 * **É tudo leitura, e de propósito.** No sistema antigo a lista de não
 * visualizados vem com um botão que os exclui em massa. Lista de conferência com
 * apagar-tudo ao lado é acidente esperando acontecer: quem quiser excluir um
 * componente faz isso na lista de componentes, um a um, onde o gesto é deliberado.
 */

import { useState } from "react"
import { usarVerificacaoDoDiagrama } from "@/api/desenhoDoDiagrama"

type Divergente = {
  diagramaComponenteId: number
  codigo: string
  apelido: string
  posicao: string
  ordem: number
  desenhadas: number
  noPinout: number
  diferenca: number
}

type Oculto = {
  diagramaComponenteId: number
  codigo: string
  apelido: string
  posicao: string
  ordem: number
  motivo: string
}

export function VerificacaoDoDiagrama({ diagramaId }: { diagramaId: number }) {
  const [aberto, setAberto] = useState(false)
  const { data, isFetching, error } = usarVerificacaoDoDiagrama(diagramaId, aberto)

  if (!aberto) {
    return (
      <button
        onClick={() => setAberto(true)}
        style={{
          height: 24, padding: "0 10px", borderRadius: 6, fontSize: 11.5,
          border: "1px solid var(--line)", background: "var(--surface)",
          color: "var(--txt2)", cursor: "pointer",
        }}
      >
        Verificar o diagrama
      </button>
    )
  }

  const v = data as (Record<string, unknown> & {
    limpo: boolean
    modelos: number
    componentesNoDiagrama: number
    ligacoesDesenhadas: number
    modelo: unknown[]
    sobrando: Divergente[]
    faltando: Divergente[]
    quantosFaltando: number
    semLigacao: Oculto[]
    quantosExcluidos: number
    quantosMarcadosParaOcultar: number
  }) | undefined

  return (
    <div style={{
      marginTop: 10, padding: 11, borderRadius: 8,
      border: "1px solid var(--line2)", background: "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <b style={{ fontSize: 12 }}>Verificação</b>
        {isFetching && <span style={{ fontSize: 11, color: "var(--txt3)" }}>conferindo…</span>}
        <button onClick={() => setAberto(false)}
                style={{ marginLeft: "auto", border: 0, background: "transparent",
                         cursor: "pointer", color: "var(--txt3)", fontSize: 11.5 }}>
          fechar
        </button>
      </div>

      {error && (
        <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "var(--erro)" }}>
          {error instanceof Error ? error.message : "Não foi possível verificar."}
        </p>
      )}

      {v && (
        <>
          <p style={{ margin: "6px 0 0", fontSize: 11.5, lineHeight: 1.55,
                      color: v.limpo ? "var(--ok, #15803d)" : "var(--erro)" }}>
            <b>{v.limpo ? "Passou nas três conferências." : "Há o que olhar."}</b>{" "}
            <span style={{ color: "var(--txt3)" }}>
              {v.componentesNoDiagrama} componente(s), {v.ligacoesDesenhadas} ligação(ões)
              desenhada(s), {v.modelos} modelo(s).
            </span>
          </p>

          <Bloco titulo="Pinout que não se aplica a um modelo"
                 quantos={v.modelo.length}
                 vazio="nenhum — é o que a publicação também exige">
            <p style={{ margin: 0, fontSize: 10.5, color: "var(--txt2)", lineHeight: 1.5 }}>
              {v.modelo.length} discrepância(s). É a mesma conferência que bloqueia a publicação.
            </p>
          </Bloco>

          <Bloco titulo="Ligações a mais no desenho" quantos={v.sobrando.length} vazio="nenhuma">
            <Linhas itens={v.sobrando} />
          </Bloco>

          <Bloco titulo="Ligações que faltam no desenho" quantos={v.quantosFaltando} vazio="nenhuma">
            <Linhas itens={v.faltando} />
            {v.quantosFaltando > v.faltando.length && (
              <p style={{ margin: "4px 0 0", fontSize: 10.5, color: "var(--txt3)" }}>
                …e mais {v.quantosFaltando - v.faltando.length}.
              </p>
            )}
          </Bloco>

          <Bloco titulo="No diagrama, mas fora do desenho" quantos={v.semLigacao.length} vazio="nenhum">
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {v.semLigacao.map((c) => (
                <span key={c.diagramaComponenteId} style={{ fontSize: 10.5, color: "var(--txt2)" }}>
                  <span className="mono">{c.codigo}</span> {c.apelido}{" "}
                  <span style={{ color: "var(--txt3)" }}>
                    ({c.posicao} {c.ordem}) — {c.motivo}
                  </span>
                </span>
              ))}
            </div>
          </Bloco>

          <p style={{ margin: "8px 0 0", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
            Na lixeira: {v.quantosExcluidos} excluído(s), {v.quantosMarcadosParaOcultar} marcado(s)
            para ocultar. A verificação <b>só informa</b> — excluir é um gesto da lista de
            componentes, um a um.
          </p>
        </>
      )}
    </div>
  )
}

function Bloco({ titulo, quantos, vazio, children }: {
  titulo: string
  quantos: number
  vazio: string
  children: React.ReactNode
}) {
  return (
    <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px dashed var(--line2)" }}>
      <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 600,
                  color: quantos > 0 ? "var(--erro)" : "var(--txt3)" }}>
        {titulo}: {quantos > 0 ? quantos : vazio}
      </p>
      {quantos > 0 && children}
    </div>
  )
}

function Linhas({ itens }: { itens: Divergente[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {itens.map((l) => (
        <span key={l.diagramaComponenteId} style={{ fontSize: 10.5, color: "var(--txt2)" }}>
          <span className="mono">{l.codigo}</span> {l.apelido}{" "}
          <span style={{ color: "var(--txt3)" }}>({l.posicao} {l.ordem})</span>{" "}
          <b style={{ fontVariantNumeric: "tabular-nums" }}>
            {l.desenhadas} desenhada(s) · {l.noPinout} no pinout · {l.diferenca > 0 ? "+" : ""}{l.diferenca}
          </b>
        </span>
      ))}
    </div>
  )
}
